import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { companyApi } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { TrainingCard } from './TrainingCard.tsx';

export type PortalTrainingItem = {
  module: { id: string; title: string; description: string | null; pass_threshold: number };
  completed_at: string | null;
  score_pct: number | null;
};

// Shared by both portals; apiPrefix is '/api/me' or '/api/driver/me'. Status
// is server-derived (a passed attempt IS the completion), so the tab needs no
// submissions/owner queries and no WizardContext.
export function TrainingTab({ apiPrefix }: { apiPrefix: string }) {
  const listQueryKey = useMemo(() => [`${apiPrefix}/training`] as const, [apiPrefix]);
  const itemsQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () =>
      companyApi.get<{ training_items: PortalTrainingItem[] }>(`${apiPrefix}/training`),
    // Admins can (de)activate modules any time; the global client disables
    // focus refetching, so refresh whenever the tab is (re)entered.
    refetchOnMount: 'always',
  });

  if (itemsQuery.isLoading) return <CenteredSpinner />;
  if (itemsQuery.error) return <ErrorMessage error={itemsQuery.error} />;

  const items = itemsQuery.data?.training_items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        title="No training assigned"
        description="Training assigned to you will appear here."
      />
    );
  }
  return (
    <div className="space-y-4">
      {items.map((item) => (
        <TrainingCard
          key={item.module.id}
          item={item}
          apiPrefix={apiPrefix}
          listQueryKey={listQueryKey}
        />
      ))}
    </div>
  );
}
